import React, { useState, useRef, useCallback } from 'react';
import { Modal, Button, Card, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

/**
 * Image Cropper Component for mole images
 * Allows users to crop images to focus on a single mole before upload
 */
const ImageCropper = ({ 
  show, 
  onHide, 
  imageFile, 
  onCropComplete,
  aspectRatio = 1 // Square aspect ratio for mole images
}) => {
  const { t } = useTranslation();
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [imageSrc, setImageSrc] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const imgRef = useRef(null);

  // Load image when file changes
  React.useEffect(() => {
    if (imageFile) {
      const reader = new FileReader();
      reader.addEventListener('load', () => setImageSrc(reader.result));
      reader.readAsDataURL(imageFile);
    }
  }, [imageFile]);

  // Initialize crop when image loads
  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 80, // Start with 80% of image
        },
        aspectRatio,
        width,
        height
      ),
      width,
      height
    );
    setCrop(crop);
  }, [aspectRatio]);

  // Convert canvas to blob/file
  const getCroppedImg = (image, crop, fileName) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const pixelRatio = window.devicePixelRatio;

    canvas.width = crop.width * pixelRatio * scaleX;
    canvas.height = crop.height * pixelRatio * scaleY;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.error('Canvas is empty');
            return;
          }
          blob.name = fileName;
          resolve(blob);
        },
        'image/jpeg',
        0.9 // High quality
      );
    });
  };

  // Handle crop completion
  const handleCropComplete = async () => {
    if (!completedCrop || !imgRef.current) {
      return;
    }

    setIsProcessing(true);
    try {
      const croppedImageBlob = await getCroppedImg(
        imgRef.current,
        completedCrop,
        imageFile.name
      );
      
      // Create new file from cropped blob
      const croppedFile = new File([croppedImageBlob], imageFile.name, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      onCropComplete(croppedFile);
      onHide();
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle skip cropping
  const handleSkipCrop = () => {
    onCropComplete(imageFile);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('skinMoles.imageCropper.title')}</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <Alert variant="info" className="mb-3">
          <Alert.Heading>{t('skinMoles.imageCropper.instruction')}</Alert.Heading>
          <p className="mb-0">{t('skinMoles.imageCropper.description')}</p>
        </Alert>

        {imageSrc && (
          <Card>
            <Card.Body className="text-center">
              <div style={{ maxHeight: '500px', overflow: 'auto' }}>
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={aspectRatio}
                  minWidth={100}
                  minHeight={100}
                >
                  <img
                    ref={imgRef}
                    alt="Crop me"
                    src={imageSrc}
                    style={{ maxWidth: '100%', maxHeight: '400px' }}
                    onLoad={onImageLoad}
                  />
                </ReactCrop>
              </div>
              
              <div className="mt-3">
                <small className="text-muted">
                  {t('skinMoles.imageCropper.tip')}
                </small>
              </div>
            </Card.Body>
          </Card>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          {t('skinMoles.imageCropper.cancel')}
        </Button>
        <Button variant="outline-primary" onClick={handleSkipCrop}>
          {t('skinMoles.imageCropper.skip')}
        </Button>
        <Button 
          variant="primary" 
          onClick={handleCropComplete}
          disabled={!completedCrop || isProcessing}
        >
          {isProcessing ? t('skinMoles.imageCropper.processing') : t('skinMoles.imageCropper.crop')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ImageCropper;
